import * as React from "react"
import {
  Controller,
  ControllerProps,
  FieldPath,
  FieldValues,
  FormProvider,
  useFormContext,
} from "react-hook-form"
import * as LabelPrimitive from "@radix-ui/react-label"
import { Slot } from "@radix-ui/react-slot"

import { Label } from "@/components/ui/label"
import { cn } from "@/helpers/classNames"
import { useFieldErrorMessage } from "@/hooks/errorMessage"

const Form = FormProvider

type FormFieldContextValue<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  disabled?: boolean
  label: string
  name: TName
  required?: boolean
}

const FormFieldContext = React.createContext<FormFieldContextValue>(
  {} as FormFieldContextValue,
)

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  disabled,
  label,
  render,
  ...controllerProps
}: {
  disabled?: boolean
  label: string
} & ControllerProps<TFieldValues, TName>) => {
  const { name, rules } = controllerProps
  const required = Boolean(rules?.required)

  // `disabled` is applied to `field` instead of `Controller`: passing it to
  // `Controller` makes RHF update form state during render.
  return (
    <FormFieldContext.Provider value={{ disabled, name, label, required }}>
      <Controller
        {...controllerProps}
        render={(renderProps) =>
          render({
            ...renderProps,
            field: { ...renderProps.field, disabled },
          })
        }
      />
    </FormFieldContext.Provider>
  )
}

const useFormField = () => {
  const fieldContext = React.useContext(FormFieldContext)
  const itemContext = React.useContext(FormItemContext)
  const { formState, getFieldState } = useFormContext()

  const fieldState = getFieldState(fieldContext.name, formState)

  if (!fieldContext) {
    throw new Error("useFormField should be used within <FormField>")
  }
  const { disabled, label, name, required } = fieldContext

  const { id } = itemContext

  return {
    id,
    name,
    label,
    required,
    disabled,
    formItemId: `${id}-form-item`,
    formDescriptionId: `${id}-form-item-description`,
    formMessageId: `${id}-form-item-message`,
    ...fieldState,
  }
}

type FormItemContextValue = {
  id: string
}

const FormItemContext = React.createContext<FormItemContextValue>(
  {} as FormItemContextValue,
)

function FormItem({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLDivElement>
} & React.HTMLAttributes<HTMLDivElement>) {
  const id = React.useId()

  return (
    <FormItemContext.Provider value={{ id }}>
      <div ref={ref} className={cn("space-y-1", className)} {...props} />
    </FormItemContext.Provider>
  )
}

function FormLabel({
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof LabelPrimitive.Root>>
} & React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>) {
  const { disabled, error, formItemId, required } = useFormField()

  return (
    <Label
      ref={ref}
      className={cn(error && "text-danger-300", className)}
      htmlFor={formItemId}
      required={required}
      variant={disabled ? "disabled" : undefined}
      {...props}
      data-unmask
    />
  )
}

function FormControl({
  ref,
  ...props
}: {
  ref?: React.Ref<React.ElementRef<typeof Slot>>
} & React.ComponentPropsWithoutRef<typeof Slot>) {
  const { error, formDescriptionId, formItemId, formMessageId } = useFormField()

  return (
    <Slot
      ref={ref}
      id={formItemId}
      aria-describedby={
        !error
          ? `${formDescriptionId}`
          : `${formDescriptionId} ${formMessageId}`
      }
      aria-invalid={!!error}
      {...props}
    />
  )
}

function FormMessage({
  children,
  className,
  ref,
  ...props
}: {
  ref?: React.Ref<HTMLParagraphElement>
} & React.HTMLAttributes<HTMLParagraphElement>) {
  const { error, formMessageId, label } = useFormField()
  const { getErrorMessage } = useFieldErrorMessage()

  const body = getErrorMessage(error?.type, label) // default implementation: error ? String(error?.message) : children
  if (!body) {
    return null
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn("text-sm font-medium text-danger-300", className)}
      {...props}
    >
      {body}
    </p>
  )
}

export { Form, FormControl, FormField, FormItem, FormLabel, FormMessage }
